import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import { usersAPI } from '../../services/api';
import { translateLocation } from '../../utils/translationUtils';
import UserAvatar from '../../components/UserAvatar';
import Modal from '../../components/Modal';
import './Users.css';

const Users = () => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  const outletContext = useOutletContext() || {};
  const { selectedLocation } = outletContext;
  const { data, addUser, enrollStudentUser, updateUser, deleteUser } = useData();

  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showStudentEnrollment, setShowStudentEnrollment] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeRoleFilter, setActiveRoleFilter] = useState('all');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [expandedCohorts, setExpandedCohorts] = useState({});
  const managerLocation = (currentUser?.location || '').toLowerCase();
  const defaultLocation = currentUser?.role === 'manager'
    ? (managerLocation || 'paramaribo')
    : (selectedLocation && selectedLocation !== 'all' ? selectedLocation : 'paramaribo');
  const availableCohorts = (data.cohorts || []).filter((cohort) => {
    if (currentUser?.role === 'manager') {
      return String(cohort.location || '').toLowerCase() === managerLocation;
    }
    if (!selectedLocation || selectedLocation === 'all') {
      return true;
    }
    return String(cohort.location || '').toLowerCase() === String(selectedLocation).toLowerCase();
  });
  const createEmptyStudentEnrollment = () => ({
    firstName: '',
    familyName: '',
    maidenName: '',
    otherNames: '',
    dateOfBirth: '',
    placeOfBirth: '',
    address: '',
    city: '',
    mobile: '',
    telephone: '',
    email: '',
    opleiding: '',
    vooropleiding: '',
    vooropleidingAnders: '',
    location: defaultLocation,
    relevantOpleidingen: '',
    relevantTrainingen: '',
    relevanteWerkverklaring: '',
    profilePhotoUrl: '',
    cohortId: '',
    password: '',
    confirmPassword: ''
  });
  const createEditableUser = (portalUser) => ({
    ...portalUser,
    firstName: portalUser.firstName || '',
    lastName: portalUser.lastName || '',
    phone: portalUser.phone || portalUser.studentProfile?.mobile || '',
    profilePhotoUrl: portalUser.profilePhotoUrl || '',
    address: portalUser.address || '',
    dateOfBirth: portalUser.dateOfBirth ? String(portalUser.dateOfBirth).slice(0, 10) : '',
    familyName: portalUser.studentProfile?.familyName || portalUser.lastName || '',
    maidenName: portalUser.studentProfile?.maidenName || '',
    otherNames: portalUser.studentProfile?.otherNames || '',
    placeOfBirth: portalUser.studentProfile?.placeOfBirth || '',
    city: portalUser.studentProfile?.city || '',
    mobile: portalUser.studentProfile?.mobile || portalUser.phone || '',
    telephone: portalUser.studentProfile?.telephone || '',
    opleiding: portalUser.studentProfile?.opleiding || '',
    vooropleiding: portalUser.studentProfile?.vooropleiding || '',
    vooropleidingAnders: portalUser.studentProfile?.vooropleidingAnders || '',
    relevantOpleidingen: portalUser.studentProfile?.relevantOpleidingen || '',
    relevantTrainingen: portalUser.studentProfile?.relevantTrainingen || '',
    relevanteWerkverklaring: portalUser.studentProfile?.relevanteWerkverklaring || '',
    cohortId: portalUser.cohort?.id || '',
    email: portalUser.email || '',
    newPassword: ''
  });
  const [newUser, setNewUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'instructor',
    profilePhotoUrl: '',
    location: defaultLocation
  });
  const [studentEnrollment, setStudentEnrollment] = useState(createEmptyStudentEnrollment());

  const sortUsersByLastName = (users = []) => {
    return [...users].sort((a, b) => {
      const aLast = String(a.studentProfile?.familyName || a.lastName || '').trim();
      const bLast = String(b.studentProfile?.familyName || b.lastName || '').trim();
      const byLast = aLast.localeCompare(bLast, 'nl', { sensitivity: 'base' });
      if (byLast !== 0) return byLast;

      const aFirst = String(a.firstName || '').trim();
      const bFirst = String(b.firstName || '').trim();
      return aFirst.localeCompare(bFirst, 'nl', { sensitivity: 'base' });
    });
  };

  useEffect(() => {
    const roleParam = String(searchParams.get('role') || '').toLowerCase();
    if (['all', 'admin', 'manager', 'instructor', 'student'].includes(roleParam)) {
      setActiveRoleFilter(roleParam);
    }

    const qParam = searchParams.get('q');
    if (qParam !== null) {
      setSearchTerm(String(qParam));
    }
  }, [searchParams]);

  const allUsers = useMemo(() => {
    return (data.users || []).map((u) => ({
      ...u,
      name: u.fullName || `${u.lastName || ''} ${u.firstName || ''}`.trim(),
      location: (u.location || '').toLowerCase()
    }));
  }, [data.users]);

  const filteredUsers = useMemo(() => {
    const source = (!selectedLocation || selectedLocation === 'all')
      ? allUsers
      : allUsers.filter((u) => (u.location || '').toLowerCase() === selectedLocation.toLowerCase());

    const byRole = activeRoleFilter === 'all' ? source : source.filter((u) => u.role === activeRoleFilter);
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const searched = !normalizedSearch ? byRole : byRole.filter((u) => {
      const haystack = [u.name, u.email, u.role, u.location].join(' ').toLowerCase();
      return haystack.includes(normalizedSearch);
    });

    return sortUsersByLastName(searched);
  }, [allUsers, selectedLocation, activeRoleFilter, searchTerm]);

  const usersByRole = useMemo(() => {
    return {
      admin: filteredUsers.filter((u) => u.role === 'admin'),
      manager: filteredUsers.filter((u) => u.role === 'manager'),
      instructor: filteredUsers.filter((u) => u.role === 'instructor'),
      student: filteredUsers.filter((u) => u.role === 'student')
    };
  }, [filteredUsers]);

  const studentCohortGroups = useMemo(() => {
    const cohortsById = new Map((data.cohorts || []).map((cohort) => [String(cohort.id || cohort._id), cohort]));
    const groups = new Map();

    (usersByRole.student || []).forEach((student) => {
      const cohortId = String(student.cohort?.id || student.cohort?._id || student.cohort || 'none');
      const cohortInfo = cohortsById.get(cohortId);
      const key = cohortId || 'none';
      if (!groups.has(key)) {
        groups.set(key, {
          id: key,
          name: cohortInfo?.name || 'Geen cohort',
          code: cohortInfo?.code || '',
          location: cohortInfo?.location || '',
          students: []
        });
      }
      groups.get(key).students.push(student);
    });

    groups.forEach((group) => {
      group.students = sortUsersByLastName(group.students);
    });

    return Array.from(groups.values()).sort((a, b) => a.name.localeCompare(b.name, 'nl'));
  }, [data.cohorts, usersByRole.student]);

  const canCreateUsers = currentUser?.role === 'admin' || currentUser?.role === 'manager';
  const allowedCreateRoles = currentUser?.role === 'manager'
    ? ['instructor']
    : ['admin', 'manager', 'instructor'];

  const canDeleteUser = (targetUser) => {
    if (!targetUser || targetUser?.id === currentUser?.id) return false;
    if (currentUser?.role === 'admin') return true;
    return false;
  };

  const canEditUser = (targetUser) => {
    if (!targetUser) return false;
    if (currentUser?.role === 'admin') return true;
    if (currentUser?.role === 'manager') {
      const targetLocation = (targetUser.location || '').toLowerCase();
      return ['student', 'instructor'].includes(targetUser.role) && targetLocation === managerLocation;
    }
    return false;
  };

  const canResetPassword = (targetUser) => {
    if (!targetUser) return false;
    if (currentUser?.role === 'admin') return true;

    if (currentUser?.role === 'manager') {
      const isSelf = String(targetUser.id) === String(currentUser.id || currentUser._id);
      if (isSelf) {
        return targetUser.role === 'manager';
      }
      const targetLocation = (targetUser.location || '').toLowerCase();
      return ['student', 'instructor'].includes(targetUser.role) && targetLocation === managerLocation;
    }

    return false;
  };

  const getRoleLabel = (role) => t(`roles.${role}`);

  useEffect(() => {
    if (!feedbackMessage) return undefined;
    const timer = setTimeout(() => setFeedbackMessage(''), 2500);
    return () => clearTimeout(timer);
  }, [feedbackMessage]);

  const handleProfilePhotoFile = (file, setter) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      window.alert('Selecteer een afbeeldingbestand.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      window.alert('Afbeelding is te groot. Maximaal 2MB toegestaan.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setter((prev) => ({ ...prev, profilePhotoUrl: String(reader.result || '') }));
    };
    reader.readAsDataURL(file);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUser.firstName || !newUser.lastName || !newUser.email || !newUser.password || !newUser.role) return;

    const payload = {
      ...newUser,
      location: currentUser?.role === 'manager' ? (managerLocation || newUser.location) : newUser.location
    };

    try {
      await addUser(payload);
      setFeedbackMessage('Gebruiker aangemaakt');
      setNewUser({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        role: 'instructor',
        profilePhotoUrl: '',
        location: defaultLocation
      });
      setShowCreateUser(false);
    } catch (error) {
      window.alert(error.message || 'Gebruiker aanmaken mislukt');
    }
  };

  const handleStudentEnrollment = async (e) => {
    e.preventDefault();
    if (studentEnrollment.password !== studentEnrollment.confirmPassword) {
      window.alert('Wachtwoorden komen niet overeen.');
      return;
    }

    const payload = {
      ...studentEnrollment,
      location: currentUser?.role === 'manager'
        ? (managerLocation || studentEnrollment.location)
        : studentEnrollment.location
    };
    delete payload.confirmPassword;

    try {
      await enrollStudentUser(payload);
      setFeedbackMessage('Student ingeschreven en account aangemaakt');
      setStudentEnrollment(createEmptyStudentEnrollment());
      setShowStudentEnrollment(false);
    } catch (error) {
      window.alert(error.message || 'Studentinschrijving mislukt');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm(`${t('common.confirm')}?`)) return;
    try {
      await deleteUser(userId);
      setFeedbackMessage('Gebruiker verwijderd');
    } catch (error) {
      window.alert(error.message || 'Gebruiker verwijderen mislukt');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editingUser?.id) return;
    try {
      await updateUser(editingUser.id, {
        email: editingUser.email,
        password: editingUser.newPassword || undefined,
        firstName: editingUser.firstName,
        lastName: editingUser.role === 'student' ? (editingUser.familyName || editingUser.lastName) : editingUser.lastName,
        location: editingUser.location,
        phone: editingUser.role === 'student' ? editingUser.mobile : editingUser.phone,
        profilePhotoUrl: editingUser.profilePhotoUrl,
        address: editingUser.address,
        dateOfBirth: editingUser.dateOfBirth,
        cohortId: editingUser.role === 'student' ? (editingUser.cohortId || '') : undefined,
        studentProfile: editingUser.role === 'student' ? {
          familyName: editingUser.familyName,
          maidenName: editingUser.maidenName,
          otherNames: editingUser.otherNames,
          placeOfBirth: editingUser.placeOfBirth,
          city: editingUser.city,
          mobile: editingUser.mobile,
          telephone: editingUser.telephone,
          opleiding: editingUser.opleiding,
          vooropleiding: editingUser.vooropleiding,
          vooropleidingAnders: editingUser.vooropleidingAnders,
          relevantOpleidingen: editingUser.relevantOpleidingen,
          relevantTrainingen: editingUser.relevantTrainingen,
          relevanteWerkverklaring: editingUser.relevanteWerkverklaring
        } : undefined
      });
      setFeedbackMessage('Gebruiker bijgewerkt');
      setEditingUser(null);
    } catch (error) {
      window.alert(error.message || 'Gebruiker bijwerken mislukt');
    }
  };

  const handleResetPassword = async (portalUser) => {
    if (!portalUser?.id) return;
    if (!window.confirm(`Wachtwoord resetten voor ${portalUser.name || portalUser.email}?`)) return;

    try {
      const result = await usersAPI.resetPassword(portalUser.id);
      const temporaryPassword = result?.temporaryPassword;
      const summary = temporaryPassword
        ? `Tijdelijk wachtwoord: ${temporaryPassword}`
        : 'Wachtwoord is gereset.';
      window.alert(summary);
      setFeedbackMessage(`Wachtwoord gereset voor ${portalUser.name || portalUser.email}`);
    } catch (error) {
      window.alert(error.message || 'Wachtwoord reset mislukt');
    }
  };

  const handleSendResetEmail = async (portalUser) => {
    if (!portalUser?.id) return;
    if (!window.confirm(`Reset e-mail versturen naar ${portalUser.email}?`)) return;

    try {
      await usersAPI.sendResetEmail(portalUser.id);
      setFeedbackMessage(`Reset e-mail verzonden naar ${portalUser.email}`);
    } catch (error) {
      window.alert(error.message || 'Reset e-mail verzenden mislukt');
    }
  };

  const renderSection = (title, roleKey) => {
    if (roleKey === 'student') {
      return (
        <div className="user-section">
          <h2>{title}</h2>
          {studentCohortGroups.length === 0 ? (
            <p className="no-users">{t('user.noUsers')}</p>
          ) : (
            <div className="cohort-group-list">
              {studentCohortGroups.map((cohort) => {
                const isNoCohortGroup = cohort.id === 'none';
                const isExpanded = isNoCohortGroup ? true : !!expandedCohorts[cohort.id];
                return (
                  <section key={cohort.id} className="cohort-group-card">
                    {isNoCohortGroup ? (
                      <div className="cohort-group-toggle">
                        <div>
                          <strong>{cohort.name}</strong>
                        </div>
                        <span>{cohort.students.length} studenten</span>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="cohort-group-toggle"
                        onClick={() => setExpandedCohorts((prev) => ({ ...prev, [cohort.id]: !prev[cohort.id] }))}
                      >
                        <div>
                          <strong>{cohort.name}</strong>
                          {cohort.code ? <span className="cohort-code"> ({cohort.code})</span> : null}
                          {cohort.location ? <span className="cohort-location"> - {translateLocation(t, String(cohort.location).toLowerCase())}</span> : null}
                        </div>
                        <span>{cohort.students.length} studenten {isExpanded ? '▲' : '▼'}</span>
                      </button>
                    )}

                    {isExpanded && (
                      <div className="user-grid">
                        {cohort.students.map((portalUser) => (
                          <div key={portalUser.id} className="user-card">
                            <UserAvatar user={portalUser} size="md" className="user-avatar" />
                            <div className="user-info">
                              <span className="user-name">{portalUser.name}</span>
                              <span className="user-email">{portalUser.email}</span>
                              {(portalUser.studentProfile?.mobile || portalUser.phone) && (
                                <span className="user-email">Mobiel: {portalUser.studentProfile?.mobile || portalUser.phone}</span>
                              )}
                              {portalUser.studentProfile?.telephone && (
                                <span className="user-email">Telefoon: {portalUser.studentProfile?.telephone}</span>
                              )}
                              <span className="user-location">{translateLocation(t, (portalUser.location || '').toLowerCase())}</span>
                            </div>
                            <div className="user-actions">
                              <span className={`user-role role-${portalUser.role}`}>{getRoleLabel(portalUser.role)}</span>
                              <div className="user-action-icons">
                                {(canEditUser(portalUser) || canResetPassword(portalUser)) && (
                                  <>
                                    {canEditUser(portalUser) && (
                                    <button
                                      className="icon-btn"
                                      onClick={() => setEditingUser(createEditableUser(portalUser))}
                                      title="Bewerken"
                                      aria-label="Gebruiker bewerken"
                                    >
                                      ✏️
                                    </button>
                                    )}
                                    {canResetPassword(portalUser) && (
                                    <button
                                      className="icon-btn"
                                      type="button"
                                      onClick={() => handleResetPassword(portalUser)}
                                      title="Reset wachtwoord"
                                      aria-label="Wachtwoord resetten"
                                    >
                                      🔒
                                    </button>
                                    )}
                                    {canResetPassword(portalUser) && (
                                    <button
                                      className="icon-btn"
                                      type="button"
                                      onClick={() => handleSendResetEmail(portalUser)}
                                      title="Verstuur reset e-mail"
                                      aria-label="Reset e-mail versturen"
                                    >
                                      📧
                                    </button>
                                    )}
                                  </>
                                )}
                                {canDeleteUser(portalUser) && (
                                  <button
                                    className="icon-btn delete-icon-btn"
                                    onClick={() => handleDeleteUser(portalUser.id)}
                                    title="Verwijderen"
                                    aria-label="Gebruiker verwijderen"
                                  >
                                    🗑️
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    const roleUsers = usersByRole[roleKey] || [];
    return (
      <div className="user-section">
        <h2>{title}</h2>
        {roleUsers.length === 0 ? (
          <p className="no-users">{t('user.noUsers')}</p>
        ) : (
          <div className="user-grid">
            {roleUsers.map((portalUser) => (
              <div key={portalUser.id} className="user-card">
                <UserAvatar user={portalUser} size="md" className="user-avatar" />
                <div className="user-info">
                  <span className="user-name" title={portalUser.name}>{portalUser.name}</span>
                  <span className="user-email" title={portalUser.email}>{portalUser.email}</span>
                  {(portalUser.studentProfile?.mobile || portalUser.phone) && (
                    <span className="user-email">Mobiel: {portalUser.studentProfile?.mobile || portalUser.phone}</span>
                  )}
                  {portalUser.studentProfile?.telephone && (
                    <span className="user-email">Telefoon: {portalUser.studentProfile?.telephone}</span>
                  )}
                  <span className="user-location">{translateLocation(t, (portalUser.location || '').toLowerCase())}</span>
                </div>
                <div className="user-actions">
                  <span className={`user-role role-${portalUser.role}`}>{getRoleLabel(portalUser.role)}</span>
                  <div className="user-action-icons">
                    {(canEditUser(portalUser) || canResetPassword(portalUser)) && (
                      <>
                        {canEditUser(portalUser) && (
                        <button
                          className="icon-btn"
                          onClick={() => setEditingUser(createEditableUser(portalUser))}
                          title="Bewerken"
                          aria-label="Gebruiker bewerken"
                        >
                          ✏️
                        </button>
                        )}
                        {canResetPassword(portalUser) && (
                        <button
                          className="icon-btn"
                          type="button"
                          onClick={() => handleResetPassword(portalUser)}
                          title="Reset wachtwoord"
                          aria-label="Wachtwoord resetten"
                        >
                          🔒
                        </button>
                        )}
                        {canResetPassword(portalUser) && (
                        <button
                          className="icon-btn"
                          type="button"
                          onClick={() => handleSendResetEmail(portalUser)}
                          title="Verstuur reset e-mail"
                          aria-label="Reset e-mail versturen"
                        >
                          📧
                        </button>
                        )}
                      </>
                    )}
                    {canDeleteUser(portalUser) && (
                      <button
                        className="icon-btn delete-icon-btn"
                        onClick={() => handleDeleteUser(portalUser.id)}
                        title="Verwijderen"
                        aria-label="Gebruiker verwijderen"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="users-page">
      <div className="page-header">
        <h1>{t('admin.users')}</h1>
        {canCreateUsers && (
          <div className="header-actions">
            <button className="btn-secondary" onClick={() => setShowCreateUser((prev) => !prev)}>
              {t('user.create')}
            </button>
            <button className="btn-primary" onClick={() => setShowStudentEnrollment(true)}>
              Student Inschrijven
            </button>
          </div>
        )}
      </div>

      <div className="permission-note" style={{ marginBottom: '1rem', color: '#4b5563' }}>
        {currentUser?.role === 'manager'
          ? t('user.permissions.managerScope')
          : t('user.permissions.adminScope')}
      </div>

      <div className="users-toolbar">
        <input
          type="text"
          className="users-search"
          placeholder="Zoek op naam, email, rol of locatie"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          className="users-filter"
          value={activeRoleFilter}
          onChange={(e) => setActiveRoleFilter(e.target.value)}
        >
          <option value="all">Alle rollen</option>
          <option value="admin">Admin</option>
          <option value="manager">Manager</option>
          <option value="instructor">Instructeur</option>
          <option value="student">Student</option>
        </select>
      </div>

      {feedbackMessage && <div className="feedback-success">{feedbackMessage}</div>}

      <Modal isOpen={showCreateUser && canCreateUsers} onClose={() => setShowCreateUser(false)} title={t('user.create')}>
          <form onSubmit={handleCreateUser}>
            <div className="form-row">
              <div className="form-group">
                <label>{t('user.firstName')}</label>
                <input
                  type="text"
                  value={newUser.firstName}
                  onChange={(e) => setNewUser((prev) => ({ ...prev, firstName: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('user.lastName')}</label>
                <input
                  type="text"
                  value={newUser.lastName}
                  onChange={(e) => setNewUser((prev) => ({ ...prev, lastName: e.target.value }))}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('auth.email')}</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser((prev) => ({ ...prev, email: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>Wachtwoord *</label>
                <input
                  type="password"
                  minLength={8}
                  value={newUser.password}
                  onChange={(e) => setNewUser((prev) => ({ ...prev, password: e.target.value }))}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('user.role')}</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser((prev) => ({ ...prev, role: e.target.value }))}
                >
                  {allowedCreateRoles.includes('admin') && <option value="admin">{t('auth.roles.admin')}</option>}
                  {allowedCreateRoles.includes('manager') && <option value="manager">{t('auth.roles.manager')}</option>}
                  {allowedCreateRoles.includes('instructor') && <option value="instructor">{t('auth.roles.instructor')}</option>}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('location.title')}</label>
                <select
                  value={newUser.location}
                  onChange={(e) => setNewUser((prev) => ({ ...prev, location: e.target.value }))}
                  disabled={currentUser?.role === 'manager'}
                  required
                >
                  {(data.locations || []).map((loc) => (
                    <option key={loc.id} value={loc.id}>{translateLocation(t, loc.id)}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Profielfoto URL (optioneel)</label>
              <input
                type="url"
                value={newUser.profilePhotoUrl}
                onChange={(e) => setNewUser((prev) => ({ ...prev, profilePhotoUrl: e.target.value }))}
                placeholder="https://..."
              />
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleProfilePhotoFile(e.target.files?.[0], setNewUser)}
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">{t('common.save')}</button>
              <button type="button" className="btn-secondary" onClick={() => setShowCreateUser(false)}>
                {t('common.cancel')}
              </button>
            </div>
          </form>
      </Modal>

      <Modal
        isOpen={showStudentEnrollment && canCreateUsers}
        onClose={() => setShowStudentEnrollment(false)}
        title="Student Inschrijven"
      >
        <form onSubmit={handleStudentEnrollment}>
          <div className="form-row">
            <div className="form-group">
              <label>Voornaam *</label>
              <input
                type="text"
                value={studentEnrollment.firstName}
                onChange={(e) => setStudentEnrollment((prev) => ({ ...prev, firstName: e.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <label>Familienaam *</label>
              <input
                type="text"
                value={studentEnrollment.familyName}
                onChange={(e) => setStudentEnrollment((prev) => ({ ...prev, familyName: e.target.value }))}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Meisjesnaam</label>
              <input
                type="text"
                value={studentEnrollment.maidenName}
                onChange={(e) => setStudentEnrollment((prev) => ({ ...prev, maidenName: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>Andere Namen</label>
              <input
                type="text"
                value={studentEnrollment.otherNames}
                onChange={(e) => setStudentEnrollment((prev) => ({ ...prev, otherNames: e.target.value }))}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Geboortedatum *</label>
              <input
                type="date"
                value={studentEnrollment.dateOfBirth}
                onChange={(e) => setStudentEnrollment((prev) => ({ ...prev, dateOfBirth: e.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <label>Geboorteplaats *</label>
              <input
                type="text"
                value={studentEnrollment.placeOfBirth}
                onChange={(e) => setStudentEnrollment((prev) => ({ ...prev, placeOfBirth: e.target.value }))}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Adres *</label>
              <input
                type="text"
                value={studentEnrollment.address}
                onChange={(e) => setStudentEnrollment((prev) => ({ ...prev, address: e.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <label>Woonplaats *</label>
              <input
                type="text"
                value={studentEnrollment.city}
                onChange={(e) => setStudentEnrollment((prev) => ({ ...prev, city: e.target.value }))}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Mobiel *</label>
              <input
                type="text"
                value={studentEnrollment.mobile}
                onChange={(e) => setStudentEnrollment((prev) => ({ ...prev, mobile: e.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <label>Telefoon</label>
              <input
                type="text"
                value={studentEnrollment.telephone}
                onChange={(e) => setStudentEnrollment((prev) => ({ ...prev, telephone: e.target.value }))}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>E-mailadres *</label>
              <input
                type="email"
                value={studentEnrollment.email}
                onChange={(e) => setStudentEnrollment((prev) => ({ ...prev, email: e.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <label>{t('location.title')} *</label>
              <select
                value={studentEnrollment.location}
                onChange={(e) => setStudentEnrollment((prev) => ({ ...prev, location: e.target.value, cohortId: '' }))}
                disabled={currentUser?.role === 'manager'}
                required
              >
                {(data.locations || []).map((loc) => (
                  <option key={loc.id} value={loc.id}>{translateLocation(t, loc.id)}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Cohort</label>
            <select
              value={studentEnrollment.cohortId}
              onChange={(e) => setStudentEnrollment((prev) => ({ ...prev, cohortId: e.target.value }))}
            >
              <option value="">Geen cohort</option>
              {availableCohorts
                .filter((cohort) => !studentEnrollment.location || cohort.location === studentEnrollment.location)
                .map((cohort) => (
                  <option key={cohort.id} value={cohort.id}>{cohort.name}{cohort.code ? ` (${cohort.code})` : ''}</option>
                ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Opleiding *</label>
              <select
                value={studentEnrollment.opleiding}
                onChange={(e) => setStudentEnrollment((prev) => ({ ...prev, opleiding: e.target.value }))}
                required
              >
                <option value="">Selecteer...</option>
                <option value="Masters">Masters</option>
                <option value="HBO Bedrijfskunde">HBO Bedrijfskunde</option>
              </select>
            </div>
            <div className="form-group">
              <label>Vooropleiding *</label>
              <select
                value={studentEnrollment.vooropleiding}
                onChange={(e) => setStudentEnrollment((prev) => ({ ...prev, vooropleiding: e.target.value, vooropleidingAnders: e.target.value === 'Anders' ? prev.vooropleidingAnders : '' }))}
                required
              >
                <option value="">Selecteer...</option>
                <option value="VWO">VWO</option>
                <option value="HAVO">HAVO</option>
                <option value="IMEAO">IMEAO</option>
                <option value="NATIN">NATIN</option>
                <option value="Kweekschool">Kweekschool</option>
                <option value="IOL">IOL</option>
                <option value="Anders">Anders</option>
              </select>
            </div>
          </div>

          {studentEnrollment.vooropleiding === 'Anders' && (
            <div className="form-group">
              <label>In geval van Anders: welke studie? *</label>
              <input
                type="text"
                value={studentEnrollment.vooropleidingAnders}
                onChange={(e) => setStudentEnrollment((prev) => ({ ...prev, vooropleidingAnders: e.target.value }))}
                required
              />
            </div>
          )}

          <div className="form-group">
            <label>Profielfoto URL (optioneel)</label>
            <input
              type="url"
              value={studentEnrollment.profilePhotoUrl}
              onChange={(e) => setStudentEnrollment((prev) => ({ ...prev, profilePhotoUrl: e.target.value }))}
              placeholder="https://..."
            />
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleProfilePhotoFile(e.target.files?.[0], setStudentEnrollment)}
            />
          </div>

          <div className="form-group">
            <label>Relevante Opleidingen</label>
            <textarea
              rows={2}
              value={studentEnrollment.relevantOpleidingen}
              onChange={(e) => setStudentEnrollment((prev) => ({ ...prev, relevantOpleidingen: e.target.value }))}
            />
          </div>

          <div className="form-group">
            <label>Relevante Trainingen</label>
            <textarea
              rows={2}
              value={studentEnrollment.relevantTrainingen}
              onChange={(e) => setStudentEnrollment((prev) => ({ ...prev, relevantTrainingen: e.target.value }))}
            />
          </div>

          <div className="form-group">
            <label>Relevante Werkverklaring</label>
            <textarea
              rows={2}
              value={studentEnrollment.relevanteWerkverklaring}
              onChange={(e) => setStudentEnrollment((prev) => ({ ...prev, relevanteWerkverklaring: e.target.value }))}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Wachtwoord *</label>
              <input
                type="password"
                minLength={8}
                value={studentEnrollment.password}
                onChange={(e) => setStudentEnrollment((prev) => ({ ...prev, password: e.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <label>Bevestig wachtwoord *</label>
              <input
                type="password"
                minLength={8}
                value={studentEnrollment.confirmPassword}
                onChange={(e) => setStudentEnrollment((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                required
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary">Student aanmaken</button>
            <button type="button" className="btn-secondary" onClick={() => setShowStudentEnrollment(false)}>
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={!!editingUser} onClose={() => setEditingUser(null)} title={t('common.edit')}>
        {editingUser && (
          <form onSubmit={handleUpdateUser}>
            <div className="form-row">
              <div className="form-group">
                <label>{t('user.firstName')}</label>
                <input
                  type="text"
                  value={editingUser.firstName || ''}
                  onChange={(e) => setEditingUser((prev) => ({ ...prev, firstName: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>{editingUser.role === 'student' ? 'Familienaam' : t('user.lastName')}</label>
                <input
                  type="text"
                  value={editingUser.role === 'student' ? (editingUser.familyName || '') : (editingUser.lastName || '')}
                  onChange={(e) => setEditingUser((prev) => (
                    editingUser.role === 'student'
                      ? { ...prev, familyName: e.target.value, lastName: e.target.value }
                      : { ...prev, lastName: e.target.value }
                  ))}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('auth.email')}</label>
                <input
                  type="email"
                  value={editingUser.email || ''}
                  onChange={(e) => setEditingUser((prev) => ({ ...prev, email: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>Nieuw wachtwoord</label>
                <input
                  type="password"
                  minLength={8}
                  value={editingUser.newPassword || ''}
                  onChange={(e) => setEditingUser((prev) => ({ ...prev, newPassword: e.target.value }))}
                  placeholder="Leeg laten om niet te wijzigen"
                />
              </div>
            </div>

            {editingUser.role === 'student' && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>Meisjesnaam</label>
                    <input type="text" value={editingUser.maidenName || ''} onChange={(e) => setEditingUser((prev) => ({ ...prev, maidenName: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>Andere Namen</label>
                    <input type="text" value={editingUser.otherNames || ''} onChange={(e) => setEditingUser((prev) => ({ ...prev, otherNames: e.target.value }))} />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Geboortedatum</label>
                    <input type="date" value={editingUser.dateOfBirth || ''} onChange={(e) => setEditingUser((prev) => ({ ...prev, dateOfBirth: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>Geboorteplaats</label>
                    <input type="text" value={editingUser.placeOfBirth || ''} onChange={(e) => setEditingUser((prev) => ({ ...prev, placeOfBirth: e.target.value }))} />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('common.address')}</label>
                    <input type="text" value={editingUser.address || ''} onChange={(e) => setEditingUser((prev) => ({ ...prev, address: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>Woonplaats</label>
                    <input type="text" value={editingUser.city || ''} onChange={(e) => setEditingUser((prev) => ({ ...prev, city: e.target.value }))} />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Mobiel</label>
                    <input type="text" value={editingUser.mobile || ''} onChange={(e) => setEditingUser((prev) => ({ ...prev, mobile: e.target.value, phone: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>Telefoon</label>
                    <input type="text" value={editingUser.telephone || ''} onChange={(e) => setEditingUser((prev) => ({ ...prev, telephone: e.target.value }))} />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Opleiding</label>
                    <select value={editingUser.opleiding || ''} onChange={(e) => setEditingUser((prev) => ({ ...prev, opleiding: e.target.value }))}>
                      <option value="">Selecteer...</option>
                      <option value="Masters">Masters</option>
                      <option value="HBO Bedrijfskunde">HBO Bedrijfskunde</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Vooropleiding</label>
                    <select value={editingUser.vooropleiding || ''} onChange={(e) => setEditingUser((prev) => ({ ...prev, vooropleiding: e.target.value, vooropleidingAnders: e.target.value === 'Anders' ? prev.vooropleidingAnders : '' }))}>
                      <option value="">Selecteer...</option>
                      <option value="VWO">VWO</option>
                      <option value="HAVO">HAVO</option>
                      <option value="IMEAO">IMEAO</option>
                      <option value="NATIN">NATIN</option>
                      <option value="Kweekschool">Kweekschool</option>
                      <option value="IOL">IOL</option>
                      <option value="Anders">Anders</option>
                    </select>
                  </div>
                </div>

                {editingUser.vooropleiding === 'Anders' && (
                  <div className="form-group">
                    <label>In geval van Anders: welke studie?</label>
                    <input type="text" value={editingUser.vooropleidingAnders || ''} onChange={(e) => setEditingUser((prev) => ({ ...prev, vooropleidingAnders: e.target.value }))} />
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('location.title')}</label>
                    <select
                      value={(editingUser.location || '').toLowerCase()}
                      onChange={(e) => setEditingUser((prev) => ({ ...prev, location: e.target.value, cohortId: '' }))}
                      disabled={currentUser?.role === 'manager'}
                    >
                      {(data.locations || []).map((loc) => (
                        <option key={loc.id} value={loc.id}>{translateLocation(t, loc.id)}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Cohort</label>
                    <select value={editingUser.cohortId || ''} onChange={(e) => setEditingUser((prev) => ({ ...prev, cohortId: e.target.value }))}>
                      <option value="">Geen cohort</option>
                      {availableCohorts
                        .filter((cohort) => !editingUser.location || cohort.location === editingUser.location)
                        .map((cohort) => (
                          <option key={cohort.id} value={cohort.id}>{cohort.name}{cohort.code ? ` (${cohort.code})` : ''}</option>
                        ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Relevante Opleidingen</label>
                  <textarea rows={2} value={editingUser.relevantOpleidingen || ''} onChange={(e) => setEditingUser((prev) => ({ ...prev, relevantOpleidingen: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Relevante Trainingen</label>
                  <textarea rows={2} value={editingUser.relevantTrainingen || ''} onChange={(e) => setEditingUser((prev) => ({ ...prev, relevantTrainingen: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Relevante Werkverklaring</label>
                  <textarea rows={2} value={editingUser.relevanteWerkverklaring || ''} onChange={(e) => setEditingUser((prev) => ({ ...prev, relevanteWerkverklaring: e.target.value }))} />
                </div>
              </>
            )}

            {editingUser.role !== 'student' && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('common.phone')}</label>
                    <input
                      type="text"
                      value={editingUser.phone || ''}
                      onChange={(e) => setEditingUser((prev) => ({ ...prev, phone: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('location.title')}</label>
                    <select
                      value={(editingUser.location || '').toLowerCase()}
                      onChange={(e) => setEditingUser((prev) => ({ ...prev, location: e.target.value }))}
                      disabled={currentUser?.role === 'manager'}
                    >
                      {(data.locations || []).map((loc) => (
                        <option key={loc.id} value={loc.id}>{translateLocation(t, loc.id)}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>{t('common.address')}</label>
                  <input type="text" value={editingUser.address || ''} onChange={(e) => setEditingUser((prev) => ({ ...prev, address: e.target.value }))} />
                </div>
              </>
            )}

            <div className="form-group">
              <label>Profielfoto URL (optioneel)</label>
              <input
                type="url"
                value={editingUser.profilePhotoUrl || ''}
                onChange={(e) => setEditingUser((prev) => ({ ...prev, profilePhotoUrl: e.target.value }))}
                placeholder="https://..."
              />
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleProfilePhotoFile(e.target.files?.[0], setEditingUser)}
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary">{t('common.save')}</button>
              <button type="button" className="btn-secondary" onClick={() => setEditingUser(null)}>{t('common.cancel')}</button>
            </div>
          </form>
        )}
      </Modal>

      <div className="user-sections">
        {(activeRoleFilter === 'all' || activeRoleFilter === 'admin') && renderSection(t('user.sections.admin'), 'admin')}
        {(activeRoleFilter === 'all' || activeRoleFilter === 'manager') && renderSection(t('user.sections.manager'), 'manager')}
        {(activeRoleFilter === 'all' || activeRoleFilter === 'instructor') && renderSection(t('user.sections.instructor'), 'instructor')}
        {(activeRoleFilter === 'all' || activeRoleFilter === 'student') && renderSection(t('user.sections.student'), 'student')}
      </div>
    </div>
  );
};

export default Users;
